import React, { useMemo } from 'react';
import { Globe } from 'lucide-react';

export const FEATURED_TIMEZONES = [
  { id: 'Europe/Madrid', label: 'Madrid / Barcelona (Spain)' },
  { id: 'Europe/Rome', label: 'Rome / Milan (Italy)' },
  { id: 'Europe/London', label: 'London / Lisbon' },
  { id: 'America/Mexico_City', label: 'Mexico City (CDMX)' },
  { id: 'America/Bogota', label: 'Bogotá / Lima / Quito' },
  { id: 'America/New_York', label: 'New York / Miami (ET)' },
  { id: 'America/Chicago', label: 'Chicago / Monterrey (CT)' },
  { id: 'America/Los_Angeles', label: 'Los Angeles / Tijuana (PT)' },
  { id: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires / Santiago' },
  { id: 'America/Sao_Paulo', label: 'São Paulo' },
  { id: 'UTC', label: 'UTC' },
];

export const detectUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
  } catch {
    return 'Europe/Madrid';
  }
};

export const getUtcOffsetLabel = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date());
    const offsetPart = parts.find((p) => p.type === 'timeZoneName');
    return offsetPart ? offsetPart.value : '';
  } catch {
    return '';
  }
};

export const formatInstantInZone = (isoOrDate, tz, options = {}) => {
  if (!isoOrDate) return '';
  try {
    const dt = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: tz || 'Europe/Madrid',
      ...options,
    }).format(dt);
  } catch {
    return String(isoOrDate);
  }
};

const TimezoneSelect = ({ value, onChange, label = 'Timezone', compact = false }) => {
  const options = useMemo(() => {
    const seen = new Set(FEATURED_TIMEZONES.map((t) => t.id));
    const list = [...FEATURED_TIMEZONES];
    if (value && !seen.has(value)) {
      list.unshift({ id: value, label: value.replace(/_/g, ' ') });
    }
    return list.map((item) => {
      const offset = getUtcOffsetLabel(item.id);
      return {
        ...item,
        display: offset ? `${item.label} (${offset})` : item.label,
      };
    });
  }, [value]);

  return (
    <div className={`inline-flex items-center gap-2 ${compact ? '' : 'bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm'}`}>
      <Globe className="w-4 h-4 text-primary flex-shrink-0" />
      {label && !compact && (
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}:</span>
      )}
      <select
        aria-label={label || 'Select timezone'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm font-semibold text-gray-800 focus:outline-none cursor-pointer pr-2"
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.display}
          </option>
        ))}
      </select>
    </div>
  );
};

export default TimezoneSelect;
